import type {
  HubCatalog,
  ChatRequest,
  ChatResponse,
  HubModule,
  HubSearchResponse,
  HubSearchResult,
  HubSourceBrief,
} from "./types";

export type SearchIndexItem = Omit<HubSearchResult, "score"> & { text: string };

async function fetchJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(path, {
    signal,
    headers: { accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export async function getCatalog(signal?: AbortSignal): Promise<HubCatalog> {
  try {
    return await fetchJson<HubCatalog>("/api/catalog", signal);
  } catch (error) {
    if (signal?.aborted) throw error;
    return fetchJson<HubCatalog>("/data/catalog.json", signal);
  }
}

export async function getModule(id: string, signal?: AbortSignal): Promise<HubModule> {
  try {
    return await fetchJson<HubModule>(`/api/modules/${encodeURIComponent(id)}`, signal);
  } catch (error) {
    if (signal?.aborted) throw error;
    return fetchJson<HubModule>(`/data/modules/${encodeURIComponent(id)}.json`, signal);
  }
}

export async function getSearchIndex(signal?: AbortSignal): Promise<SearchIndexItem[]> {
  return fetchJson<SearchIndexItem[]>("/data/search-index.json", signal);
}

export async function getSourceBrief(
  documentId: string,
  signal?: AbortSignal,
): Promise<HubSourceBrief> {
  return fetchJson<HubSourceBrief>(`/api/briefs/${encodeURIComponent(documentId)}`, signal);
}

export async function searchHub(
  query: string,
  options: { sectionId?: string; type?: string; limit?: number } = {},
  signal?: AbortSignal,
): Promise<HubSearchResponse> {
  const params = new URLSearchParams({ q: query });
  if (options.sectionId) params.set("section", options.sectionId);
  if (options.type) params.set("type", options.type);
  if (options.limit) params.set("limit", String(options.limit));

  try {
    return await fetchJson<HubSearchResponse>(`/api/search?${params}`, signal);
  } catch (error) {
    if (signal?.aborted) throw error;
    const index = await fetchJson<SearchIndexItem[]>("/data/search-index.json", signal);
    return searchLocal(index, query, options);
  }
}

export async function sendChatMessage(
  input: ChatRequest,
  signal?: AbortSignal,
): Promise<ChatResponse> {
  const response = await fetch("/api/chat", {
    method: "POST",
    signal,
    headers: {
      accept: "application/json",
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: unknown } | null;
    const detail = typeof payload?.error === "string" ? `: ${payload.error}` : "";
    throw new Error(`Chat request failed (${response.status})${detail}`);
  }
  return response.json() as Promise<ChatResponse>;
}

function searchLocal(
  index: SearchIndexItem[],
  query: string,
  options: { sectionId?: string; type?: string; limit?: number },
): HubSearchResponse {
  const normalizedQuery = normalize(query);
  const terms = [...new Set(normalizedQuery.split(" ").filter((term) => term.length > 1))];
  const results = index
    .filter((item) => !options.sectionId || item.sectionId === options.sectionId)
    .filter((item) => !options.type || item.type === options.type)
    .map((item) => ({ item, score: scoreItem(item, normalizedQuery, terms) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score || a.item.title.localeCompare(b.item.title));

  return {
    query,
    total: results.length,
    results: results.slice(0, options.limit || 24).map(({ item, score }) => {
      const { text: _text, ...result } = item;
      return { ...result, score };
    }),
    mode: "local-fallback",
  };
}

function scoreItem(item: SearchIndexItem, query: string, terms: string[]) {
  const title = normalize(item.title);
  const text = normalize(item.text || item.excerpt);
  let score = 0;
  if (title === query) score += 120;
  if (title.startsWith(query)) score += 60;
  if (title.includes(query)) score += 42;
  if (text.includes(query)) score += 24;
  for (const term of terms) {
    if (title.includes(term)) score += 10;
    if (text.includes(term)) score += 4;
  }
  if (terms.length > 1 && !terms.every((term) => title.includes(term) || text.includes(term))) {
    score *= 0.45;
  }
  if (score > 0 && item.type === "module") score += 4;
  if (score > 0 && item.type === "chapter") score += 2;
  return Math.round(score * 100) / 100;
}

function normalize(value: string) {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}
